import { Alert, Box, Button, LinearProgress, Typography } from "@mui/material";
import { useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { AuthContext } from "../../../contexts/auth";
import { useProject } from "../../../contexts/project.context";
import { useHomeSummary } from "../hooks/useHomeSummary";
import { useHomeTracking } from "../hooks/useHomeTracking";
import type { HomeFilterKey, HomeScope } from "../types/home.types";
import {
  homeListUrl,
  homeProjectsUrl,
  homeUrl,
  isHomeScope,
} from "../utils/homeFilters";
import { AttentionCards } from "./AttentionCards";
import { CountTiles } from "./CountTiles";
import { HomeHeader } from "./HomeHeader";
import { LoadPanel } from "./LoadPanel";
import { MovementsPanel } from "./MovementsPanel";
import { NextSevenDays } from "./NextSevenDays";
import { PhasesPanel } from "./PhasesPanel";
import { HOME_COLORS } from "./homeTheme";

function SectionTitle({ title, aside }: { title: string; aside?: string }) {
  return (
    <Box
      display="flex"
      alignItems="baseline"
      justifyContent="space-between"
      gap={2}
    >
      <Typography component="h2" sx={{ fontSize: 16, fontWeight: 500 }}>
        {title}
      </Typography>
      {aside && (
        <Typography sx={{ fontSize: 12, color: HOME_COLORS.muted }}>
          {aside}
        </Typography>
      )}
    </Box>
  );
}

export function HomePage() {
  const history = useHistory();
  const location = useLocation();
  const { user, actionOptions } = useContext(AuthContext);
  const { clearProject } = useProject();

  const urlScope = new URLSearchParams(location.search).get("escopo");
  const requestedScope = isHomeScope(urlScope) ? urlScope : undefined;
  const { data, isFetching, isError, refetch } = useHomeSummary(requestedScope);
  const tracking = useHomeTracking(requestedScope);
  // A Api devolve o escopo efetivo: uma visão fora do perfil (ex.: Coordenador
  // com ?escopo=todos na URL) volta para o padrão dele.
  const scope: HomeScope | undefined =
    requestedScope && (!data || data.allowedScopes.includes(requestedScope))
      ? requestedScope
      : data?.scope;

  // O escopo vive na URL: "Voltar para Início" e o voltar do navegador
  // reabrem a tela no mesmo escopo. replace evita empilhar cada troca.
  const changeScope = (next: HomeScope) => history.replace(homeUrl(next));
  const openList = (key: HomeFilterKey) =>
    scope && history.push(homeProjectsUrl(key, scope));
  const openProject = (id: string) => history.push(`/projeto?projectId=${id}`);
  // Os blocos da TASK 2 só valem quando chegam do mesmo escopo da tela
  // (keepPreviousData mantém o anterior enquanto o novo carrega).
  const trackingData =
    tracking.data && tracking.data.scope === scope ? tracking.data : undefined;

  return (
    <Box
      display="flex"
      flexDirection="column"
      flex={1}
      gap={3}
      py={4}
      px={3}
      bgcolor="#F9FCFF"
      sx={{ overflowY: "auto", minWidth: 0 }}
    >
      <HomeHeader
        userName={user?.name}
        today={data?.today}
        profile={data?.profile}
        defaultScope={data?.defaultScope}
        allowedScopes={data?.allowedScopes}
        scope={scope}
        onScopeChange={changeScope}
        canCreateProject={!!actionOptions.createProjetos}
        onNewProject={() => {
          // Mesmo fluxo do botão da lista de Projetos
          history.push("/projeto");
          clearProject();
        }}
      />

      <Box height={4}>
        {(isFetching || tracking.isFetching) && <LinearProgress />}
      </Box>

      {isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Tentar de novo
            </Button>
          }
        >
          Não foi possível carregar os indicadores do Início.
        </Alert>
      )}

      {tracking.isError && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => tracking.refetch()}
            >
              Tentar de novo
            </Button>
          }
        >
          Não foi possível carregar as fases, a carga e as movimentações.
        </Alert>
      )}

      <AttentionCards
        today={data?.today}
        cards={data?.cards}
        onOpen={openList}
      />

      <Box display="flex" flexWrap="wrap" gap={2}>
        <NextSevenDays
          today={data?.today}
          agenda={data?.agenda}
          onOpenProject={openProject}
          onSeeAll={() => openList("proximos7")}
        />
        <PhasesPanel
          phases={trackingData?.phases}
          onOpenStatus={(status) =>
            scope &&
            history.push(
              homeListUrl({ scope, status: status.key, label: status.label })
            )
          }
        />
      </Box>

      {scope && scope !== "meus" && trackingData?.load && (
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-start">
          <LoadPanel
            // Trocar de escopo recolhe a linha expandida.
            key={scope}
            scope={scope}
            today={trackingData.today}
            load={trackingData.load}
            onOpenProject={openProject}
            onOpenCoordinator={(coordinator, cell) =>
              history.push(
                homeListUrl({
                  scope,
                  filter: "ativos",
                  coordinator: coordinator.id,
                  cell: cell?.id,
                  label: cell
                    ? `${coordinator.name} · ${cell.name}`
                    : coordinator.name,
                })
              )
            }
            onOpenCell={(cell) =>
              history.push(
                homeListUrl({
                  scope,
                  filter: "ativos",
                  cell: cell.id,
                  label: cell.name,
                })
              )
            }
          />
          <MovementsPanel
            today={trackingData.today}
            movements={trackingData.movements ?? []}
            showCell={scope === "todos"}
            onOpenProject={openProject}
          />
        </Box>
      )}

      <Box
        component="section"
        aria-label="Pós-evento parado"
        display="flex"
        flexDirection="column"
        gap={1.5}
      >
        <SectionTitle title="Pós-evento parado" />
        <CountTiles
          onOpen={openList}
          tiles={[
            {
              key: "realizadoParado",
              title: "Realizado sem Pré-fechamento",
              support: "Há mais de 5 dias",
              value: data?.posEvento.realizadoParado,
            },
            {
              key: "aguardandoCliente",
              title: "Aguardando Cliente",
              support: "Há mais de 10 dias",
              value: data?.posEvento.aguardandoCliente,
            },
            {
              key: "pendenteAuditoria",
              title: "Pendente Auditoria",
              support: "Faturados ainda não auditados",
              value: data?.posEvento.pendenteAuditoria,
            },
          ]}
        />
      </Box>

      {data?.perdas && (
        <Box
          component="section"
          aria-label="Perdas e cancelamentos"
          display="flex"
          flexDirection="column"
          gap={1.5}
        >
          <SectionTitle
            title="Perdas e cancelamentos"
            aside={`Últimos 30 dias, ${data.perdas.total} projetos encerrados sem execução`}
          />
          <CountTiles
            onOpen={openList}
            tiles={[
              {
                key: "perdidos",
                title: "Perdidos",
                support: "Perdido",
                value: data.perdas.perdidos,
              },
              {
                key: "cancelados",
                title: "Cancelados",
                support:
                  "Inclui Aprovado/Cancelado, Cancelado/Cobrado e Cancelado/Auditado",
                value: data.perdas.cancelados,
              },
              {
                key: "declinados",
                title: "Declinados",
                support: "Declinado",
                value: data.perdas.declinados,
              },
            ]}
          />
        </Box>
      )}
    </Box>
  );
}
